<template>
	<div class="display-flex flex-column height-100 pb-8">
		<v-expansion-panels v-model="panel" flat accordion>
			<ExpansionPanel :title="'Basics'">
				<SettingSwitch
					:title="'Label'"
					:placeholder="'Type your title here'"
					:switchValue.sync="titleEnabled"
					:textFieldValue.sync="titleValue"
				/>
				<SettingSwitch class="mt-2" title="Required" :switchValue.sync="requiredEnabled" />
			</ExpansionPanel>
			<ExpansionPanel :title="'Data'">
				<v-col cols="12" class="text-left pa-1">
					<div v-if="!selectedApp">
						<h2 class="med">Choose app and item</h2>
						<v-autocomplete
							ref="searchApp"
							class="ac"
							item-text="service_name"
							item-value="id"
							height="52"
							color="#5C8DF5"
							outlined
							hide-details
							single-line
							label="Search for an app"
							:menu-props="menuProps"
							:search-input.sync="searchString"
							:items="appItems"
						>
							<template v-slot:prepend-inner>
								<SIcon class="search-icon" name="autocomplete-search" />
							</template>
							<template v-slot:append>
								<span></span>
							</template>
						</v-autocomplete>
						<v-row no-gutters v-if="filteredItems.length > 0" style="overflow: auto">
							<v-col
								cols="6"
								role="button"
								v-for="item in filteredItems"
								:key="item.id"
								class="py-5 result_block text-center"
								@click="selectApp(item)"
							>
								<v-row no-gutters class="">
									<v-col class="text-center" cols="12">
										<v-card class="ac_card inline-block ma-0 pa-0">
											<div class="centerdXY">
												<SIcon :width="22" :name="item.service_slug" />
											</div>
										</v-card>
									</v-col>
									<v-col class="text-center" cols="12">
										<h3 class="reg inline-block">{{ item.service_name }}</h3>
									</v-col>
								</v-row>
							</v-col>
						</v-row>
					</div>
					<div v-if="selectedApp">
						<v-row no-gutters class="choose_item_type">
							<v-col cols="12">
								<v-row no-gutters class="align-center">
									<v-col cols="7" class="text-left">
										<v-row align="center">
											<v-col cols="auto">
												<div class="ac_card inline-block ma-0 pa-0">
													<div class="centerdXY">
														<SIcon :width="16" :name="selectedApp.service_slug" />
													</div>
												</div>
											</v-col>
											<v-col>
												<h3 class="reg inline-block pl-6" style="vertical-align: middle">
													<span>{{ selectedApp.service_name }}</span> <br />
													<span class="py-2" v-if="selectedType">
														<h5 class="med inline-block" style="vertical-align: middle">{{ selectedType.title }}</h5>
													</span>
												</h3>
											</v-col>
										</v-row>
									</v-col>
									<v-col class="text-right" cols="5">
										<v-btn rounded outlined color="#D1D9E9" class="chg" height="30px" @click="changeActionOrService">Change</v-btn>
									</v-col>
								</v-row>
							</v-col>
							<v-col cols="12">
								<v-divider class="my-3"></v-divider>
							</v-col>
							<v-col cols="12" v-if="!selectedType">
								<h2 class="med mt-4">Choose item type</h2>
								<SelectionButton class="my-2" v-for="item in appActions" :key="item.id" @click="selectedType = item">
									{{ formatSearchChoice(item.noun) }}
								</SelectionButton>
							</v-col>
						</v-row>

						<v-row no-gutters class="mb-5" v-if="selectedType && doesRequiresAuth(selectedApp)">
							<!-- we do not use profile selector to show the chosen profile -->
							<ProfileSelectionMenu
								widgetFieldMode
								showLockedIndication
								:narrowLayout="true"
								:showLoadingStatus="false"
								:serviceId="selectedApp.id"
								:credentialsData="credentialsData"
								:sourceFieldUUID="sourceFieldUUID"
								:configurationId="configurationId"
								:credentialsType="credentialsType"
								:overrideSelectProfile="setProfile"
								:serviceVersionId="selectedAppVersion"
								:profileId="selectedProfileId"
								:hasEditPermissions="hasEditPermissions"
							/>
						</v-row>

						<v-row no-gutters v-if="selectedType && (selectedProfile || !doesRequiresAuth(selectedApp))">
							<v-col cols="12" v-if="selectedProfile && !(selectedType && doesRequiresAuth(selectedApp))">
								<v-row no-gutters class="align-center">
									<v-col class="text-left">
										<h3 class="med">Profile:</h3>
									</v-col>
									<v-col>
										<h3 class="reg inline-block pl-8">
											<span>{{ selectedProfile.connection_name }}</span>
										</h3>
									</v-col>
									<v-col class="text-right">
										<v-btn rounded outlined color="#D1D9E9" class="chg" height="30px" @click="changeProfile">Change</v-btn>
									</v-col>
								</v-row>
								<v-divider class="my-3"></v-divider>
							</v-col>
							<v-col cols="12">
								<SearchFieldSettingsRequiredFields
									v-if="readyForRequiredFields"
									:selectedProfile="selectedProfile"
									:selectedApp="selectedApp"
									:selectedType="selectedType"
									:localField="localField"
									:selectedAppVersion="selectedAppVersion"
									@updateLocalField="({ key, value }) => setLocalFieldKey(key, value)"
								/>
							</v-col>
							<v-col cols="12">
								<SwitchOption option-name="Multiple Selection" :switch-model.sync="userSelectionMultiple" />
								<PrefillSearchTermSettings :localField="localField" @updateLocalField="({ key, value }) => setLocalFieldKey(key, value)" />
								<CustomUserSettings :localField="localField" @updateLocalField="({ key, value }) => setLocalFieldKey(key, value)" />
							</v-col>
						</v-row>
					</div>
				</v-col>
			</ExpansionPanel>
		</v-expansion-panels>
	</div>
</template>

<script>
import AppService from '@/utils/AppService';
import { pluralize } from '@/utils/language-service';
import { mapGetters, mapActions } from 'vuex';
import SwitchOption from '../FieldOptions/SwitchOption';
import SettingSwitch from '../WidgetSettings/SettingSwitch';
import ExpansionPanel from '@/components/BlockEditorWidget/FieldSettings/WidgetSettings/ExpansionPanel';

import CustomUserSettings from './CustomUserSettings';
import PrefillSearchTermSettings from './PrefillSearchTermSettings';
import { getConfigurationFieldResponse, getConfigurationFieldsIdsPerInterface } from '@/store/modules/workflowConfigurations/getters';

import { getServiceActionsByVersion } from '@/store/modules/apps/getters';
import { widgetFieldCredentials, isSavingWidget } from '@/store/modules/widgetEditor/getters';
import {
	VIRTUAL_PROFILES_IDS,
	EMBEDDED_CONNECTION_ID,
	PER_USER_CONNECTION_ID,
	PER_ACCOUNT_CONNECTION_ID,
} from '@/utils/credentials-service.const';
import { userSpecificConfigurationId, generalConfigurationId } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import { getConfigurationAndInterfaceIdByFieldUUID } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import LocalFieldMixin from '../LocalFieldMixin';
import { canEditWorkflow } from '@/store/modules/workflows/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { editNode } from '@/store/modules/selectedNode/getters';
import { selectedWorkflowId } from '@/store/modules/selectedWorkflow/getters';
import { CredentialsTypes } from '@ply-io/ply-front-shared-utilities';
export default {
	name: 'SearchFieldSettings',
	mixins: [LocalFieldMixin],
	// NOTICE: This is the special Field settings component that doesn't use the 'FieldSettingsMixin', because it is significantly different
	components: {
		SwitchOption,
		SettingSwitch,
		CustomUserSettings,
		ExpansionPanel,
		PrefillSearchTermSettings,
		ProfileSelectionMenu: () => import('@/components/BlockEditor/IntegrationEditor/ProfileSelection/ProfileSelectionMenu.vue'),
		SearchFieldSettingsRequiredFields: () => import('./SearchFieldSettingsRequiredParams'),
	},
	watch: {
		titleValue(newV) {
			this.fieldIdentifier = newV;
		},
	},
	computed: {
		...mapGetters([
			generalConfigurationId.fullPath,
			userSpecificConfigurationId.fullPath,
			getConfigurationFieldResponse.fullPath,
			getConfigurationFieldsIdsPerInterface.fullPath,
		]),
		...mapGetters([
			editNode.fullPath,
			'apps/getCredentialsForService',
			'apps/getAllServicesList',
			'apps/getService',
			getServiceActionsByVersion.fullPath,
			widgetFieldCredentials.fullPath,
			isSavingWidget.fullPath,
			getConfigurationAndInterfaceIdByFieldUUID.fullPath,
		]),
		...mapGettersShorthand({ canEditWorkflow, selectedWorkflowId }),
		showAdvanced() {
			return false;
		},
		filteredItems() {
			if (this.searchString == '') {
				return this.appItems;
			} else {
				return this.$refs.searchApp.filteredItems;
			}
		},
		menuProps() {
			return { maxHeight: 0 };
		},
		appsEntries() {
			return this['apps/getAllServicesList'];
		},
		appItems() {
			return [...this.appsEntries.filter((app) => app.has_search_action)];
		},
		selectedApp: {
			get() {
				return this['apps/getService']([this.localField.metadata.source.service]);
			},
			set(app) {
				this.setLocalFieldKey('metadata.source.service', app?.id);
				if (app) {
					this.setLocalFieldKey('metadata.source.service_version', app.version);
					const title = `Search ${app.service_name}`;
					this.updateFieldTitle(title);
					this['apps/getActionsOfApp']({ id: app.id, version: app.version });
				}
			},
		},
		selectedAppVersion: {
			get() {
				return this.localField?.metadata?.source?.service_version;
			},
			set(value) {
				this.setLocalFieldKey('metadata.source.service_version', value);
			},
		},
		selectedType: {
			get() {
				return this.appActions.find((a) => a.id === this.localField.metadata.source.action);
			},
			set(value) {
				let title;
				if (value) {
					this.setLocalFieldKey('metadata.source.action', value.id);
					this.setLocalFieldKey('metadata.options.noun', value.noun);
					title = `Search ${this.selectedApp.service_name} ${pluralize(value.noun)}`;
				} else {
					this.setLocalFieldKey('metadata.source.action', value);
					this.setLocalFieldKey('metadata.options.noun', '');
					title = `Search ${this.selectedApp.service_name}`;
				}
				this.updateFieldTitle(title);
			},
		},
		selectedProfileId() {
			let credentialsId = this.localField.metadata.source.credentials;
			if (!credentialsId) {
				const sourceFieldUuid = this.localField.metadata.source.credentials_configuration.source_field_uuid;
				if (sourceFieldUuid) {
					const conf = this[getConfigurationAndInterfaceIdByFieldUUID.fullPath](sourceFieldUuid);
					const fieldResponse = this[getConfigurationFieldResponse.fullPath](conf.configurationId, sourceFieldUuid);
					credentialsId = fieldResponse;
				}
			}
			return credentialsId;
		},
		selectedProfile: {
			get() {
				const credentialsForService = this['apps/getCredentialsForService'](this.selectedApp.id);
				if (!credentialsForService || !credentialsForService.length) {
					return null;
				}
				return credentialsForService.find((p) => p.id === this.selectedProfileId);
			},
			set(value) {
				this.setLocalFieldKey('metadata.source.credentials', value);
			},
		},
		credentialsType() {
			return this.localField.metadata.source.credentials_configuration.credentials_type;
		},
		appActions() {
			if (this.selectedApp.id && this.selectedAppVersion) {
				return this[getServiceActionsByVersion.fullPath](this.selectedApp.id, this.selectedAppVersion).filter((a) => a.type === 'search');
			}
			return [];
		},
		readyForRequiredFields() {
			return this.selectedApp && this.selectedType && this.credentialsReadyForRequiredFields;
		},
		credentialsReadyForRequiredFields() {
			return !this.doesRequiresAuth(this.selectedApp) || (this.selectedProfile && !this[isSavingWidget.fullPath]);
		},
		configurationId() {
			return this.localField.metadata.source.credentials_configuration.credentials_type === CredentialsTypes.ProfileBased
				? this[userSpecificConfigurationId.fullPath]
				: this[generalConfigurationId.fullPath]; // CredentialsTypes.AccountBased
		},
		sourceFieldUUID() {
			return this.localField.metadata.source.credentials_configuration.source_field_uuid;
		},
		credentialsData() {
			return {
				...(this.localField.metadata.source.credentials_configuration || {}),
				credentials: this.localField.metadata.source.credentials,
			};
		},
		userSelectionMultiple: {
			get() {
				return this.localField.metadata.options.user_selection.multiple;
			},
			set(value) {
				this.setLocalFieldKey('metadata.options.user_selection.multiple', value);
			},
		},
		hasEditPermissions() {
			return this.canEditWorkflow(this.selectedWorkflowId);
		},
	},
	methods: {
		...mapActions(['apps/getActionsOfApp']),
		formatSearchChoice: (string) => string.charAt(0).toUpperCase() + string.slice(1),
		selectApp(app) {
			this.selectedApp = app;
		},
		setProfile(profileID, fieldUUID) {
			if (!profileID) {
				profileID =
					this.localField.metadata.source.credentials_configuration.credentials_type === CredentialsTypes.AccountBased
						? PER_ACCOUNT_CONNECTION_ID
						: PER_USER_CONNECTION_ID; // CredentialsTypes.ProfileBased
			}
			if (VIRTUAL_PROFILES_IDS.includes(profileID)) {
				const credentialsType = profileID === PER_USER_CONNECTION_ID ? CredentialsTypes.ProfileBased : CredentialsTypes.AccountBased;
				this.setLocalFieldKey('metadata.source.credentials_configuration.source_field_uuid', fieldUUID);
				this.setLocalFieldKey('metadata.source.credentials_configuration.credentials_type', credentialsType);
				this.setLocalFieldKey('metadata.source.credentials', null); // We can have either credentials_configuration or credentials
				this.setLocalFieldKey('metadata.source.credentials_configuration.need_to_advise', true);
			} else {
				if (profileID === EMBEDDED_CONNECTION_ID) {
					profileID = null;
				}
				this.setLocalFieldKey('metadata.source.credentials', profileID);
				this.setLocalFieldKey('metadata.source.credentials_configuration.source_field_uuid', null);
				this.setLocalFieldKey('metadata.source.credentials_configuration.credentials_type', CredentialsTypes.Embedded);
				this.setLocalFieldKey('metadata.source.credentials_configuration.need_to_advise', true);
			}
		},
		changeActionOrService() {
			if (!this.selectedType) {
				this.selectedApp = null;
				this.selectedAppVersion = null;
				this.changeProfile();
			} else {
				this.selectedType = null;
			}
		},
		changeProfile() {
			this.selectedProfile = null;
		},
		updateFieldTitle(value) {
			if (!('identifier' in this.localField.metadata.options)) {
				this.fieldIdentifier = this.titleValue;
			}

			const needToUpdateIdentifier = this.fieldIdentifier === this.titleValue;

			this.titleValue = value;
			if (needToUpdateIdentifier) {
				this.fieldIdentifier = value;
			}
		},
		doesRequiresAuth(app) {
			return AppService.doesRequiresAuth(app);
		},
	},
	data: () => ({
		panel: 1,
		searchString: '',
	}),
	created() {
		if (this.selectedApp) {
			this['apps/getActionsOfApp']({ id: this.selectedApp.id, version: this.selectedAppVersion });
		}
	},
};
</script>
<style scoped>
h3.light {
	color: #6b7786;
	font-weight: 300;
}
.result_block h3 {
	transition: all 0.15s;
}
.result_block:hover h3 {
	color: #61b4f6 !important;
}
.choose_item_type .ac_card {
	width: 40px !important;
	height: 40px !important;
}

h3.med {
	color: #292829;
}
.search-icon {
	margin: -7px 4px 0 0;
}
</style>
